import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError, AxiosResponse} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
  callAlertWeb,
  callNativeWeb,
  clickMoveToAppWeb,
  footerShowEventWeb,
  hideProgressWeb,
  showProgressWeb,
  showKycRequiredPopupWeb,
  resumeUploadIDCardWeb,
  callAppLoginWeb
} from 'common/appInterface';
import { handleApplyInfoError } from 'common/applyInfoError';
import Modal from '../../components/Modal';
import stringUtils from "common/stringUtils";
import { CardProductCode, KYC_REQUIRED_POPUP_MIN_VERSION, APP_UPDATE_REQUIRED_MIN_VERSION, NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION, QUICK_CARD_REGISTER_MIN_VERSION } from '../../constants/Card';
import { WEBVIEW_RESOURCE_VERSION } from 'common/cacheOverwrite';
import CardApplyBlockModal from 'components/CardApplyBlockModal';
import { fetchRestrictedCountryCodes, isRestrictedCountry } from 'common/cardApplyRestriction';

const ProductDetailPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** Params */
  const { cardProductCode } = useParams();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productDetail, setProdcutDetail] = useState<Product>();
  const [cardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply, cardProductCode: cardProductCode });
  const [debitState, setDebitState] = useState<CardDebitState | null>(null);
  const [isModalView, setIsModalView] = useState<boolean>(false);
  const [totalPeningFeeAmount, setTotalPeningFeeAmount] = useState<string>("0");
  /** 발급 제한 국가코드 목록 */
  const [restrictedCountryCodes, setRestrictedCountryCodes] = useState<string[]>([]);
  /** 발급 제한 국가 안내 팝업 */
  const [isApplyBlockModal, setIsApplyBlockModal] = useState<boolean>(false);
  /** 진입 시 조회한 이전 접수내역(product/check) 응답 — 버튼 문구와 카드신청 클릭에서 재사용 */
  const [applyCheckResponse, setApplyCheckResponse] = useState<AxiosResponse | null>(null);
  /** 재진입(04) 신분증 신규 업로드 모드 플래그 */
  const [isIdCardApply, setIsIdCardApply] = useState<boolean>(false);
  /** 재진입(04) 신분증 업로드 중인 접수 정보 */
  const [continueApplyReceiveInfo, setContinueApplyReceiveInfo] = useState<{
    receiveSeq?: string,
    receiveDate?: string,
    cardProductCode?: string,
    designDivisionCode?: string,
    userEngName?: string,
    phoneNo?: string,
    sexDivisionCode?: string,
    birthDate?: string
  }>({});

  /** 카드상품 조회 */
  const callProductDetail = useCallback(async () => {
    await client.get("/api/app/product/" + cardProductCode, { params: location.state.query }).then(response => {
      setProdcutDetail(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    });
  }, [cardProductCode, location]);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);

  /** 이전 접수내역 확인 요청 */
  const requestApplyCheck = useCallback(() => {
    return client.get("/api/app/product/check", { params: { ...location.state.query, cardProductCode: cardProductCode }});
  }, [location.state.query, cardProductCode]);

  /** 이전 접수내역 조회 (진입 시) — 실패하면 팝업 없이 비워 두고, 카드신청 클릭 때 다시 조회한다 */
  const callApplyCheck = useCallback(async () => {
    await requestApplyCheck().then(response => {
      setApplyCheckResponse(response.data.code === "00" ? response : null);
    }).catch(() => {
      setApplyCheckResponse(null);
    });
  }, [requestApplyCheck]);

	/** 카드신청계속 클릭 */
	const clickApplyContinue = useCallback((cardApplyNotify: CardApplyNotify) => {
		showProgressWeb();

		/** 고객정보 조회 */
		client.get("/api/app/apply/info", { params: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}}).then((response) => {
			let result = response.data.response;

			if (response.data.code === "00") {
				// 발급 제한 국가
				if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
					setIsApplyBlockModal(true);
					return;
				}

				// KYC 미인증
				if (result.kycApproved === "N") {
					if (Number(location.state.query.apiVersion) >= KYC_REQUIRED_POPUP_MIN_VERSION) {
						showKycRequiredPopupWeb();
					} else {
						callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
					}
					return;
				}

				// 핀코드 입력 완료 단계 (메인 재진입과 동일)
				if (cardApplyNotify.receiveStatusCode === "04") {
					if (parseInt(location.state.query.apiVersion) >= NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION) {
						// 신규 버전: 네이티브 재진입 신분증 업로드 호출 (결과는 idUpload 이벤트로 수신)
						setIsIdCardApply(true);
						setContinueApplyReceiveInfo({
							receiveSeq: cardApplyNotify.receiveSeq,
							receiveDate: cardApplyNotify.receiveDate,
							cardProductCode: cardApplyNotify.cardProductCode,
							designDivisionCode: cardApplyNotify.designDivisionCode,
							userEngName: result.userEngName,
							phoneNo: result.phoneNo,
							sexDivisionCode: result.sexDivisionCode,
							birthDate: result.birthDate
						});
						resumeUploadIDCardWeb();
						return;
					}
					// 구버전: 핀코드 입력 완료 단계는 신분증 업로드 단계로 이동한다.
					navigate("/apply/step4", {
						state: {
							query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
							apply: {
								...location.state.apply,
								cardProductCode: cardApplyNotify?.cardProductCode,
								designDivisionCode: cardApplyNotify.designDivisionCode,
								receiveSeq: cardApplyNotify.receiveSeq,
								receiveDate: cardApplyNotify.receiveDate,
								foreignerDivisionCode: result.foreignerDivisionCode,
								isReupload: false,
								isChangeDelivery: false,
								isContinue: true,
								// 중단 팝업에서 재진입 취소 시 이 상품상세로 돌아오기 위한 정보
								continueFrom: 'productDetail',
								continueFromHistoryIdx: window.history.state?.idx,
								userEngName: result.userEngName,
								phoneNo: result.phoneNo,
								sexDivisionCode: result.sexDivisionCode,
								birthDate: result.birthDate
							},
							from: location.state.from,
							swiperIndex: location.state.swiperIndex,
							tabIndex: "1"
						}
					});
				}
				// 신분증 업로드 완료 단계는 고객정보등록 단계로 이동한다.
				else if (cardApplyNotify.receiveStatusCode === "05") {
					navigate("/apply/step5", {
						state: {
							...location.state,
							query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode},
							apply: {
								...location.state.apply,
								cardProductCode: cardApplyNotify?.cardProductCode,
								receiveSeq: cardApplyNotify.receiveSeq,
								receiveDate: cardApplyNotify.receiveDate,
								isChangeDelivery: false,
								isContinue: true,
								// 중단 팝업에서 재진입 취소 시 이 상품상세로 돌아오기 위한 정보
								continueFrom: 'productDetail',
								continueFromHistoryIdx: window.history.state?.idx,
								designDivisionCode: cardApplyNotify.designDivisionCode,
								userEngName: result.userEngName,
								phoneNo: result.phoneNo,
								sexDivisionCode: result.sexDivisionCode,
								birthDate: result.birthDate,
								foreignerDivisionCode: result.foreignerDivisionCode
							},
							userInfo: debitState
						}
					});
				}
				// 카드수령지 선택 단계는 수령지 선택으로 이동한다.
				else if (cardApplyNotify.receiveStatusCode === "06") {
					navigate("/apply/step6/method", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}, apply: { ...location.state.apply, cardProductCode: cardApplyNotify?.cardProductCode, receiveSeq: cardApplyNotify.receiveSeq, receiveDate: cardApplyNotify.receiveDate, isChangeDelivery: false, isContinue: true, continueFrom: 'productDetail', continueFromHistoryIdx: window.history.state?.idx, designDivisionCode: cardApplyNotify.designDivisionCode }, userInfo: debitState}});
				}
				// 그 이전단계는 처음부터 시작한다.
				else {
					navigate("/apply/step1", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}, apply: { ...location.state.apply, cardProductCode: cardApplyNotify.cardProductCode, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode, skipCardBenefit: true }, userInfo: debitState}});
				}
			}
			else {
				handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
	}, [navigate, location, debitState, restrictedCountryCodes, t]);

  /** 퀵카드 등록 클릭 (카드 리스트에서 상세로 진입한 경우, 기존과 같이 퀵카드 등록으로 이동) */
  const clickQuickCardRegister = useCallback(async () => {
		showProgressWeb();

		/** 고객정보 조회 */
		await client.get("/api/app/apply/info", { params: { ...location.state.query, cardProductCode: cardProductCode }}).then((response) => {
			let result = response.data.response;

			if (response.data.code === "00") {
				// 발급 제한 국가
				if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
					setIsApplyBlockModal(true);
					return;
				}
				// KYC 미인증
				if (result.kycApproved === "N") {
					if (Number(location.state.query.apiVersion) >= KYC_REQUIRED_POPUP_MIN_VERSION) {
						showKycRequiredPopupWeb();
					} else {
						callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
					}
					return;
				}
				// 퀵카드 등록
				if (Number(location.state.query.apiVersion) >= QUICK_CARD_REGISTER_MIN_VERSION) {
					clickMoveToAppWeb("N876");
				} else {
					callAlertWeb("99", "99", t('STAT01.popup05'));
				}
			}
			else {
				handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
  }, [location, cardProductCode, restrictedCountryCodes, t]);

  /** 카드신청 클릭 */
  const clickApply = useCallback(async () => {
		// 앱 버전이 카드 신청 가능 버전 미만이면 업데이트 안내 얼럿
		if (Number(location.state.query.apiVersion) < APP_UPDATE_REQUIRED_MIN_VERSION) {
			callAlertWeb("99", "99", t('STAT01.popup05'));
		}
		else {
			showProgressWeb();

			// 이전 접수내역 확인 — 진입 시 조회한 결과를 재사용하고, 없으면(조회 실패 등) 다시 조회
			await (applyCheckResponse ? Promise.resolve(applyCheckResponse) : requestApplyCheck()).then(response => {
				let result = response.data.response;

				if (response.data.code === "00") {
					// 이전 접수내역이 있을 경우
					if (result.receiveStatusNotifyCode === "Y") {
						clickApplyContinue(result);
					}
					else {
						/** 고객정보 조회 */
						client.get("/api/app/apply/info", { params: { ...location.state.query, cardProductCode: cardProductCode }}).then((response) => {
							let result = response.data.response;

							if (response.data.code === "00") {
								// 발급 제한 국가
								if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
									setIsApplyBlockModal(true);
									return;
								}
								// KYC 미인증
								if (result.kycApproved === "N") {
									if (Number(location.state.query.apiVersion) >= KYC_REQUIRED_POPUP_MIN_VERSION) {
										showKycRequiredPopupWeb();
									} else {
										callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
									}
								}
								else {
									if (productDetail?.membershipFeeCode === '1') {
										if (result.totalPendingFeeAmount === "0") {
											navigate("/apply/step1", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardProductCode}, apply: { ...cardApply, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode, skipCardBenefit: true }, userInfo: debitState}});
										} else {
											setTotalPeningFeeAmount(result.totalPendingFeeAmount);
											setIsModalView(true);
										}
									} else {
										navigate("/apply/step1", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardProductCode}, apply: { ...cardApply, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode, skipCardBenefit: true }, userInfo: debitState}});
									}
								}
							}
							else {
								handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
							}
						}).catch((e: AxiosError) => {
							callAlertWeb("ERROR", "99", e.message);
						});
					}
				}
				else {
					callAlertWeb("ERROR", response.data.code, response.data.message);
				}
			}).catch((e: AxiosError) => {
				callAlertWeb("ERROR", "99", e.message);
			}).finally(() => {
				hideProgressWeb();
			});
		}
  }, [location, navigate, cardProductCode, clickApplyContinue, cardApply, t, debitState, setIsModalView, productDetail, restrictedCountryCodes, applyCheckResponse, requestApplyCheck]);

  // 모달 닫기
  const closeModal = useCallback(() => {
    setIsModalView(false);
  }, []);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductDetail();
    callApplyCheck();
    callDebitState();
    fetchRestrictedCountryCodes(location.state.query.languageCode).then(setRestrictedCountryCodes);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  /** 재진입 신분증 업로드 정보 초기화 */
  const initContinueApplyReceiveInfo = useCallback(() => {
    setIsIdCardApply(false);
    setContinueApplyReceiveInfo({});
  }, []);

  /** 재진입(04) 신분증 업로드 결과 처리 (completedPhotoID_App (APP > WEB)) — 메인 재진입과 동일 */
  const callbackIDCardUpload = useCallback(async (event) => {
    if (!isIdCardApply) {
      return;
    }

    // App 에서 넘어온 신분증 이미지 세팅
    let idcardList = { [event.detail.idType + event.detail.photoType]: decodeURIComponent(event.detail.imageValue) };
    // 신분증구분코드 세팅 (1: 주민등록증, 3: 외국인 등록증, 4: 여권)
    let idcardDivisionCode = event.detail.idType;

    showProgressWeb();
    await client.post("/api/app/apply/idcard/upload", { ...location.state.query, receiveSeq: continueApplyReceiveInfo.receiveSeq, receiveDate: continueApplyReceiveInfo.receiveDate, idcardDivisionCode: idcardDivisionCode, idcardList: idcardList, idVerifyYn: event.detail.idVerifyYn, idVerifyFailReason: event.detail.disagreementReason }).then((response) => {
      if (response.data.code === "00") {
        navigate("/apply/step5", {
          state: { ...location.state,
            query: {...location.state.query, cardProductCode: continueApplyReceiveInfo.cardProductCode},
            apply: {
              ...location.state.apply,
              cardProductCode: continueApplyReceiveInfo.cardProductCode,
              designDivisionCode: continueApplyReceiveInfo.designDivisionCode,
              receiveSeq: continueApplyReceiveInfo.receiveSeq,
              receiveDate: continueApplyReceiveInfo.receiveDate,
              userEngName: continueApplyReceiveInfo.userEngName,
              phoneNo: continueApplyReceiveInfo.phoneNo,
              sexDivisionCode: continueApplyReceiveInfo.sexDivisionCode,
              birthDate: continueApplyReceiveInfo.birthDate,
              isContinue: false
            },
            from: location.pathname
          }
        });
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      initContinueApplyReceiveInfo();
    });
  }, [isIdCardApply, continueApplyReceiveInfo, location.pathname, location.state, navigate, initContinueApplyReceiveInfo]);

  // 신분증 업로드 이벤트 리스너 (completedPhotoID_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("idUpload", callbackIDCardUpload);
    return () => document.removeEventListener("idUpload", callbackIDCardUpload);
  }, [callbackIDCardUpload]);

  /** 재진입 신분증 업로드 화면에서 신청 취소 (cancelCardApply_App (APP > WEB)) — 접수 취소 후 상품상세 새로고침 */
  const callbackCancelCardApply = useCallback(async () => {
    showProgressWeb();
    await client.patch("/api/app/apply/cancel", {
      ...location.state.query,
      receiveSeq: continueApplyReceiveInfo.receiveSeq,
      receiveDate: continueApplyReceiveInfo.receiveDate
    }).then((response) => {
      if (response.data.code === "00") {
        callProductDetail();
        callApplyCheck();
      } else if (response.data.code === "o4") {
        callAlertWeb("ERROR", response.data.code, response.data.message);
        callAppLoginWeb();
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      initContinueApplyReceiveInfo();
    });
  }, [location.state.query, continueApplyReceiveInfo, callProductDetail, callApplyCheck, initContinueApplyReceiveInfo]);

  // 신청 취소 이벤트 리스너 (cancelCardApply_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("cancelCardApply_App", callbackCancelCardApply);
    return () => document.removeEventListener("cancelCardApply_App", callbackCancelCardApply);
  }, [callbackCancelCardApply]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <iframe
          className="productDetail"
          src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/benefit/${cardProductCode}/detail/card_detail_benefit.html?v=${WEBVIEW_RESOURCE_VERSION}&lang=${location.state.query.languageCode}`}
          allowFullScreen={true}
          title={productDetail?.cardProductCodeName}
        />
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button
              type="button"
              className="btnPrimaryTy01"
              disabled={
                !productDetail ||
                productDetail?.hasYn === 'Y' ||
                productDetail?.receiveYn === 'Y'
              }
              onClick={Number(cardProductCode) === CardProductCode.QuickCard ? clickQuickCardRegister : clickApply}>
                <span>
                  {productDetail?.hasYn === 'Y'
                    ? t('APPL02.exist')
                    : productDetail?.receiveYn === 'Y'
                      ? t('APPL02.receive')
                      : Number(cardProductCode) === CardProductCode.QuickCard
                        ? t('APPL01.listButton04', 'Register Card')
                      // 재진입(미완료 접수 04·05·06): 신청 이어하기 문구 (메인과 동일)
                      : applyCheckResponse?.data.response?.receiveStatusNotifyCode === 'Y' && ["04", "05", "06"].includes(applyCheckResponse?.data.response?.receiveStatusCode)
                        ? t('APPL01.apply02', 'Continue Card Application')
                        : t('APPL02.apply')}
                </span>
            </button>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalView}
        onRequestClose={closeModal}
        className="popTyLayer2"
        overlayClassName="dim"
        closeTimeoutMS={500}>
        <div>
          <div className="Please-pay-Pending-Fee">
            <span>
              {t('APPL02.pendingFee').replace(
                '{0}',
                stringUtils.nvlDefault(
                  stringUtils.moneyFormat(totalPeningFeeAmount)
                )
              )}
            </span>
          </div>
        </div>
        <div className="btnWrapTy02">
          <button
            type="button"
            className="btnPrimaryTy01"
            onClick={() => clickMoveToAppWeb('N039')}>
            <span>{t('SERV16.button.pay')}</span>
          </button>
        </div>
      </Modal>
      <CardApplyBlockModal isOpen={isApplyBlockModal} onClose={() => setIsApplyBlockModal(false)} />
    </div>
  );
};

export default ProductDetailPage;